import type {ReactNode} from 'react'
import {PullRequestState} from '../../../../types'
import {ClosedPR, shouldShowClosedPR} from '../pull-request-state/ClosedPR'
import {MergedPR, shouldShowMergedPR} from '../pull-request-state/MergedPR'
import {AbleToMerge, shouldShowAbleToMerge} from '../mergeability/AbleToMerge'
import {ApprovableChecks} from '../mergeability/ApprovableChecks'
import {AwaitingApprovalChecks} from '../mergeability/AwaitingApprovalChecks'
import {shouldShowDraftPR, DraftPR} from '../pull-request-state/DraftPR'
import {FailingChecks} from '../mergeability/FailingChecks'
import {shouldShowMergeBlocked, MergeBlocked} from '../mergeability/MergeBlocked'
import {shouldShowMergeConflicts, MergeConflicts} from '../mergeability/MergeConflicts'
import {PendingChecks} from '../mergeability/PendingChecks'
import {shouldShowReadyToMerge, ReadyToMerge} from '../mergeability/ReadyToMerge'
import {decideChecksItem} from '../mergeability/shared/checks-item-decider'
import {selectFailedRequiredChecks} from '../mergeability/use-failed-checks-data'
import {useMergeabilityData, type MergeabilityData} from '../mergeability/use-mergeability-data'
import {usePullRequestState} from '../../../../hooks/use-pull-request-state'

type AuthorForYouActionInput = {
  mergeabilityData: MergeabilityData | null
  pullRequestState: PullRequestState
}

type LazyAction = {
  action: ReactNode
  matches: () => boolean
}

export function useAuthorOrAssigneeDecider() {
  const mergeabilityData = useMergeabilityData()
  const pullRequestState = usePullRequestState()

  return getAuthorForYouAction({
    mergeabilityData,
    pullRequestState,
  })
}

export function getAuthorForYouAction({mergeabilityData, pullRequestState}: AuthorForYouActionInput): ReactNode {
  const decisionTree: LazyAction[] = [
    {action: <ClosedPR variant="embedded" userRole="author" />, matches: () => shouldShowClosedPR(pullRequestState)},
    {
      action: <MergedPR variant="embedded" userRole="author" />,
      matches: () => shouldShowMergedPR(pullRequestState),
    },
    {action: <DraftPR variant="embedded" userRole="author" />, matches: () => shouldShowDraftPR(pullRequestState)},
    {
      action: <FailingChecks variant="embedded" />,
      matches: () => Boolean(mergeabilityData && selectFailedRequiredChecks(mergeabilityData.statusChecks).length),
    },
    {
      action: <ApprovableChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'approvable',
    },
    {
      action: <AwaitingApprovalChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'awaiting-approval',
    },
    {action: <MergeConflicts variant="embedded" />, matches: () => shouldShowMergeConflicts(mergeabilityData)},
    {
      action: <PendingChecks variant="embedded" />,
      matches: () => decideChecksItem(mergeabilityData).kind === 'pending',
    },
    {action: <MergeBlocked variant="embedded" />, matches: () => shouldShowMergeBlocked(mergeabilityData)},
    {action: <ReadyToMerge variant="embedded" />, matches: () => shouldShowReadyToMerge(mergeabilityData)},
    {action: <AbleToMerge variant="embedded" />, matches: () => shouldShowAbleToMerge(mergeabilityData)},
  ]

  return decisionTree.find(decision => decision.matches())?.action ?? null
}
